
import { useState, useEffect, useCallback } from "react";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import { toast } from "react-toastify";
import { ExternalLink, Link } from "lucide-react";

import PostActions from "./LikeCommentActions";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PublicViewIframeModal = ({
  isOpen,
  onClose,
  url,
  title,
  description,
  type,
  userId,
  postId,
  is_Liked,
  likeCount,
  commentCount,
  onReadsUpdate,
}) => {
  const navigate=useNavigate()
  const token = localStorage.getItem("access_token");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [editedTitle, setEditedTitle] = useState(title || "");
  const [isLiked, setIsLiked] = useState(is_Liked || false);
  const [totalLikes, setTotalLikes] = useState(likeCount || 0);
  const [editedDescription, setEditedDescription] = useState(description || "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pubExpanded, setPubExpanded] = useState(false);

  const [publication, setPublication] = useState({
    title: title || "",
    description: description || "",
    userId: userId || null,
  });
  const [loading, setLoading] = useState(false);
  const [htmlContent, setHtmlContent] = useState(""); 
  const [postData, setPostData] = useState(null);

  const handleClose = async () => {
    let latestPost = postData;

    // If we haven't fetched the latest reads yet (or the fetch is still in-flight), attempt to fetch on close.
    if ((!latestPost || latestPost.reads === undefined || latestPost.reads === null) && postId && type) {
      try {
        const res = await fetch(
          `${BASE_URL}/user/publication/home/?id=${postId}&type=${type}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_X_API_KEY,
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          setPostData(data);
          latestPost = data;
        }
      } catch (err) {
        console.error("Error fetching post data on close:", err);
      }
    }

    if (onReadsUpdate && latestPost) {
      onReadsUpdate(latestPost.id, latestPost.reads || 0);
    }

    onClose();
    // Reset URL
    localStorage.removeItem('activePost');
    window.history.pushState({}, "", "/home");
  };

  useEffect(() => {
    if (isOpen && postId && type) {
      const fetchPostData = async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/user/publication/home/?id=${postId}&type=${type}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                "x-api-key": process.env.REACT_APP_X_API_KEY
              },
            }
          );
          if (res.ok) {
            const data = await res.json();
            setPostData(data);
          }
        } catch (err) {
          console.error("Error fetching post data:", err);
        }
      };
      fetchPostData();
    }
  }, [isOpen, postId, type, token]);

  // console.log("The publication:",publication)
  // console.log("The current user is:",currentUserId)
  useEffect(() => {
    if (url && /\.html?$/i.test(url)) {
      fetch(url)
        .then((res) => res.text())
        .then((text) => setHtmlContent(text))
        .catch((err) => console.error("Error loading HTML file:", err));
    }
  }, [url]);

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/user/publication/view-received-invitation-list/`,
          {
            headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY  },
          }
        );
        setInvitations(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch invitations:", err);
      }
    };

    if (isOpen && token) {
      fetchInvitations();
    }
  }, [isOpen, token]);

  const handleEdit = () => {
    setEditedTitle(publication.title || "");
    setEditedDescription(publication.description || "");
    setEditMode(true);
  };

  const handleApply = async () => {
    if (!editedTitle.trim()) {
      toast.error("Title cannot be empty");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/user/publication/publication/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
           Authorization: `Bearer ${token}`,
           "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: JSON.stringify({
          id: postId,
          title: editedTitle,
          abstract: editedDescription,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Publication updated successfully!");
        setPublication({
          title: data.data.title,
          description: data?.description || data?.abstract || "",
          userId: data.data.user,
        });
        setEditedTitle(data.data.title);
        setEditedDescription(
          data.data?.description || data.data?.abstract || ""
        );
        setEditMode(false);
      } else {
        toast.error(data?.message || "Failed to update publication");
      }
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/user/publication/publication/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
           Authorization: `Bearer ${token}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: JSON.stringify({ id: postId }),
      });
      if (res.status === 204 || res.ok) {
        toast.success("Publication deleted successfully!");
        handleClose();
      } else {
        const data = await res.json();
        toast.error(data?.message || "Failed to delete publication");
      }
    } catch (error) {
      toast.error("Deletion failed");
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/user/profile/create-profile/`, {
        headers: { Authorization: `Bearer ${token}` ,  "x-api-key": process.env.REACT_APP_X_API_KEY,},
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentUserId(data.data?.user);
        localStorage.setItem("user_id", data.data?.user);
      } else {
        console.error("");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error getting user profile.");
    }
  }, [BASE_URL, token]);

  useEffect(() => {
    if (isOpen && token) {
      fetchCurrentUser();
    }
  }, [isOpen, token, fetchCurrentUser]);

  useEffect(() => {
    const fetchSinglePublication = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/user/publication/home/?id=${postId}&type=publication`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              "x-api-key": process.env.REACT_APP_X_API_KEY
            },
          }
        );

        const data = await res.json();
        console.log('the publication data is:',data?.user)
        if (!res.ok)
          throw new Error(data.message || "Failed to fetch publication");
        setPublication({
          user_profile: data?.user_profile,
          title: data?.title || "",
          description: data?.description || data?.abstract || "",
          userId: data.user || null,
          reference:data?.reference,
        });
      } catch (error) {
        console.error("Publication fetch error:", error.message);
      }
    };

    if (isOpen && postId) fetchSinglePublication();
  }, [isOpen, postId]);

  if (!isOpen) return null;

  if (!url) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full p-6 relative">
        <p>No document to display</p>
        <button onClick={handleClose} className="mt-4 px-4 py-2 bg-gray-200 rounded">
          Close
        </button>
      </div>
    </div>
  );

  const fileType = url.split(".").pop().toLowerCase();
  const isPDF = fileType === "pdf";

  const docs = [
    {
      uri: url,
      fileType: fileType,
    },
  ];

  return (
    <div  className="fixed inset-0 z-[100000] bg-black/50 flex items-center justify-center overflow-hidden">
      <div  className="w-full h-full sm:w-screen sm:h-screen bg-white relative flex flex-col sm:flex">
         <button
     onClick={handleClose}
      className="cursor-pointer absolute top-0 left-0 sm:top-4 sm:left-4 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gray-200 rounded-full hover:bg-gray-300 z-10"
    >
      ✕
    </button>
     <div className="flex flex-col sm:flex-row w-full h-full">
        <div className="w-full h-[60vh] sm:h-full sm:w-9/12 p-2 sm:p-4 overflow-hidden flex items-center justify-center bg-gray-50">
         <div className="mt-12 w-full h-full mb-6 border rounded-lg overflow-hidden">
              {isPDF ? (
                <iframe
                  src={url}
                  title={publication.title || "PDF Viewer"}
                  className="w-full h-full border-none"
                />
              ) : /\.(png|jpe?g|gif)$/i.test(url) ? (
                <img
                  src={url}
                  alt={publication.title || "Image Viewer"}
                  className="w-full h-full object-contain"
                />
              ) : /\.html?$/i.test(url) ? (
                <div className="w-full h-full overflow-auto bg-gray-50">
                  <pre className="p-4 whitespace-pre-wrap">
                    <code>{htmlContent}</code>
                  </pre>
                </div>
              ) : (
                <DocViewer
                  documents={docs}
                  pluginRenderers={DocViewerRenderers}
                  className="w-full h-full"
                  config={{
                    header: {
                      disableHeader: true,
                      disableFileName: true,
                    },
                  }}
                />
              )}
            </div>
      </div>
      <div className="w-full sm:w-3/12 h-[40vh] sm:h-full flex flex-col bg-white overflow-hidden">
      <div className="flex-1 overflow-y-auto p-3 sm:pt-6 sm:px-4">
        {/* User Info - Mobile optimized */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <img 
                src={publication.user_profile?.profile_picture} 
                alt="User" 
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-cover border-2 border-white shadow-sm" 
                onClick={()=>navigate(`${currentUserId === publication.userId ? "/profile-view" : `/user-profile/${publication?.user_profile?.id}`}`)}
              />
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-gray-900 text-sm sm:text-base">
                {publication.user_profile?.name}
              </span>
            </div>
          </div>
           <div className="mt-3 sm:mt-4 text-base sm:text-lg  text-gray-900 mb-2 break-words">
  {pubExpanded || (publication?.description?.length || 0) <= 120
    ? publication?.description
    : publication?.description.slice(0, 120) + "..."}

  {(publication?.description?.length || 0) > 120 && (
    <span
      onClick={() => setPubExpanded(!pubExpanded)}
      className="text-blue-600 cursor-pointer ml-1 font-medium"
    >
      {pubExpanded ? " Read less" : " Read more"}
    </span>
  )}
</div>
{publication.reference && (
  <div className="my-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
    <div className="flex items-center gap-2 mb-3">
      <ExternalLink size={18} className="text-blue-500" />
      <h3 className="text-lg font-semibold text-gray-800">Reference Post</h3>
    </div>
    <a
      href={publication.reference}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
    >
      <Link size={16} className="text-gray-400 group-hover:text-blue-500" />
      <span className="text-blue-600 hover:text-blue-700 font-medium break-all">
        {publication.reference}
      </span>
    </a>
  </div>
)}

            {type === "publication" && currentUserId && (
              <div className="border-t pt-4">
                <PostActions
                  postId={postId}
                  type={type}
                  is_Liked={isLiked}
                  likeCount={totalLikes}
                  commentCount={commentCount}
                  currentUser={currentUserId}
                  onLikeChange={(postId, liked) => {
                    setIsLiked(liked);
                    setTotalLikes(prev => liked ? prev + 1 : prev - 1);
                  }}
                />
              </div>
            )}
      </div>
      </div>
     </div>
      </div>
    </div>
  );
};

export default PublicViewIframeModal;